/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Category Explorer & Filter Bar for Prompt Catalog (Task 10.2.5 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Search,
  X,
  Heart,
  Clock,
  LayoutGrid,
  List,
  RotateCcw,
  Cpu,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import { usePromptTemplateStore } from '../../stores/usePromptTemplateStore';
import { PromptCatalogService } from '../../services/prompt-catalog.service';
import { useI18n } from '../../stores/useI18nStore';

/**
 * Maps category slug to standardized high-tech Sophia vector icon.
 */
function getCategorySophiaIcon(slug: string): SophiaDefaultIconName {
  switch (slug) {
    case 'blog-longform':
      return 'brainstorm';
    case 'marketing-ads':
      return 'strategy';
    case 'email-marketing':
      return 'virtual-call';
    case 'ecommerce':
      return 'digital-wallet';
    case 'coding-technical':
      return 'binary-code';
    default:
      return 'artificial-intelligence';
  }
}

export const PromptCategoryFilterBar: React.FC = () => {
  const {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    selectedModel,
    setSelectedModel,
    activeTab,
    setActiveTab,
    favorites,
    recentlyUsed,
    viewMode,
    setViewMode,
    resetFilters,
  } = usePromptTemplateStore();
  const { t } = useI18n();

  const categories = PromptCatalogService.getCategories();
  const categoryCounts = PromptCatalogService.getCategoryCounts();

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    selectedCategory !== 'all' ||
    selectedModel !== 'all' ||
    activeTab !== 'all';

  return (
    <div className="space-y-4">
      {/* 1. Primary Row: Instant Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-foreground-muted">
          <Search className="w-4 h-4" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('templates.searchPlaceholder')}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface text-sm text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-foreground-muted hover:text-foreground"
            aria-label={t('common.clear')}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 2. Horizontal Scrollable Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        <button
          type="button"
          onClick={() => setSelectedCategory('all')}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
            selectedCategory === 'all'
              ? 'bg-primary text-primary-fg border-primary shadow-glow-primary/20'
              : 'border-surface-border bg-surface text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <SophiaIcon name="artificial-intelligence" size={14} className="shrink-0" />
          <span>{t('templates.categories.all')}</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              selectedCategory === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-muted text-foreground-muted'
            }`}
          >
            {categoryCounts.all ?? 35}
          </span>
        </button>

        {categories.map((cat) => {
          const isSelected = selectedCategory === cat.slug;
          const count = categoryCounts[cat.slug] ?? 0;

          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(cat.slug)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                isSelected
                  ? 'bg-primary text-primary-fg border-primary shadow-glow-primary/20'
                  : 'border-surface-border bg-surface text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
              }`}
            >
              <SophiaIcon name={getCategorySophiaIcon(cat.slug)} size={14} className="shrink-0" />
              <span>{cat.name}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  isSelected
                    ? 'bg-white/20 text-white'
                    : 'bg-muted text-foreground-muted'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* 3. Secondary Filter & Control Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-surface-border/60">
        {/* Quick Tabs: All | Favorites | Recent */}
        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-surface border border-surface-border">
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              activeTab === 'all'
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            {t('templates.allTab')}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('favorites')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              activeTab === 'favorites'
                ? 'bg-rose-500 text-white shadow-sm'
                : 'text-foreground-muted hover:text-rose-500'
            }`}
          >
            <Heart className="w-3.5 h-3.5 fill-current" />
            <span>{t('templates.favoritesTab')}</span>
            {favorites.length > 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  activeTab === 'favorites'
                    ? 'bg-white/20 text-white'
                    : 'bg-rose-500/10 text-rose-500'
                }`}
              >
                {favorites.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('recent')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              activeTab === 'recent'
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{t('templates.recentTab')}</span>
            {recentlyUsed.length > 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  activeTab === 'recent'
                    ? 'bg-white/20 text-white'
                    : 'bg-primary/10 text-primary'
                }`}
              >
                {recentlyUsed.length}
              </span>
            )}
          </button>
        </div>

        {/* Right side: AI Model Filter, View Mode, Reset */}
        <div className="flex items-center gap-2">
          {/* AI Model Dropdown Filter */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border border-surface-border bg-surface text-xs text-foreground-muted">
            <Cpu className="w-3.5 h-3.5 text-primary" />
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="bg-transparent text-xs text-foreground font-medium focus:outline-none cursor-pointer pr-1"
            >
              <option value="all">{t('common.all')} AI Models</option>
              <option value="gpt-4o">GPT-4o (OpenAI)</option>
              <option value="claude-3-5-sonnet">Claude 3.5 Sonnet (Anthropic)</option>
              <option value="gemini-1-5-pro">Gemini 1.5 Pro (Google)</option>
              <option value="deepseek-r1">DeepSeek R1 (Reasoning)</option>
            </select>
          </div>

          {/* View Mode Toggle: Grid vs Compact List */}
          <div className="flex items-center p-0.5 rounded-lg border border-surface-border bg-surface">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              title={t('templates.viewGrid')}
              aria-label={t('templates.viewGrid')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-primary-fg'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('compact')}
              title={t('templates.viewList')}
              aria-label={t('templates.viewList')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'compact'
                  ? 'bg-primary text-primary-fg'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Reset Filters button */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              title={t('templates.resetFiltersBtn')}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10 border border-surface-border transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              <span className="hidden sm:inline">{t('common.clear')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
